import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { DetailPageLayout } from '../../../components/layouts';
import { usePageTitle } from '../../../hooks/shared';
import AdminElectionEditor from '../../elections/components/AdminElectionEditor';
import { useAuthStore } from '../../identity/store/authStore';
import { authoritiesAPI } from '../api/authorities';
import AdminOfficalSources from '../components/AdminOfficialSources';
import AdminVoterPreparedness from '../components/AdminVoterPreparedness';

const SCRAPER_STYLES = {
  working: 'bg-emerald-100 text-emerald-700',
  broken: 'bg-red-100 text-red-700',
  not_configured: 'bg-neutral-100 text-neutral-400',
  unknown: 'bg-neutral-100 text-neutral-400',
};

const SYNC_STATUS_STYLES = {
  success: 'text-emerald-600',
  partial: 'text-amber-600',
  error: 'text-red-600',
  running: 'text-blue-600',
};

function fmt(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function ms(n) {
  if (n == null) return '—';
  return n < 1000 ? `${n}ms` : `${(n / 1000).toFixed(1)}s`;
}

function RunLogRow({ log, elections }) {
  const [open, setOpen] = useState(true);
  return (
    <>
      <div
        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-neutral-50 select-none"
        onClick={() => elections.length > 0 && setOpen((o) => !o)}
      >
        <span className="w-4 text-neutral-300 shrink-0">
          {elections.length > 0 ? (
            open ? (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-neutral-500">
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-neutral-400">
                <path
                  fillRule="evenodd"
                  d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                  clipRule="evenodd"
                />
              </svg>
            )
          ) : null}
        </span>
        <span className="text-xs text-neutral-600 whitespace-nowrap w-36">
          {fmt(log.startedAt)}
        </span>
        <span className="text-xs text-neutral-500 w-16">{ms(log.durationMs)}</span>
        <span
          className={`text-xs font-medium w-16 ${SYNC_STATUS_STYLES[log.status] ?? 'text-neutral-500'}`}
        >
          {log.status}
        </span>
        <span className="text-xs text-neutral-500">
          {log.stats
            ? `${log.stats.created ?? 0} created · ${log.stats.updated ?? 0} updated · ${log.stats.total ?? 0} total`
            : (log.errorMessage ?? '')}
        </span>
        {elections.length > 0 && (
          <span className="ml-auto text-xs text-neutral-400">
            {elections.length} election{elections.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      {open && elections.length > 0 && (
        <div className="bg-neutral-50 border-t border-neutral-100 px-6 py-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-neutral-400 border-b border-neutral-200">
                <th className="text-left pb-1.5 font-medium">Election</th>
                <th className="text-left pb-1.5 font-medium">Type</th>
                <th className="text-left pb-1.5 font-medium">Date</th>
                <th className="text-left pb-1.5 font-medium">Reg. deadline</th>
                <th className="text-left pb-1.5 font-medium">Early voting</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {elections.map((e) => (
                <tr key={e.id}>
                  <td className="py-1.5 text-neutral-800 font-medium">{e.name}</td>
                  <td className="py-1.5 text-neutral-500">{e.electionType}</td>
                  <td className="py-1.5 text-neutral-700">{fmtDate(e.electionDate)}</td>
                  <td className="py-1.5 text-neutral-500">{fmtDate(e.registrationDeadline)}</td>
                  <td className="py-1.5 text-neutral-500">
                    {e.earlyVotingStart
                      ? `${fmtDate(e.earlyVotingStart)} – ${fmtDate(e.earlyVotingEnd)}`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ScraperSection({
  authority,
  sd,
  runMutation,
  runError,
  runResult,
  setRunError,
  setRunResult,
}) {
  const syncLogs = sd?.syncLogs ?? [];
  const a = authority;

  return !sd ? (
    <p className="text-base text-neutral-400 italic">
      No scraper registered for{' '}
      {a?.jurisdictionType !== 'state' ? 'this jurisdiction type (state-level only)' : a?.state}.
    </p>
  ) : (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-neutral-400 mb-0.5">Scraper key</p>
          <p className="font-mono text-xs text-neutral-700">{sd.key}</p>
        </div>
        <button
          onClick={() => {
            setRunError(null);
            setRunResult(null);
            runMutation.mutate();
          }}
          disabled={runMutation.isPending}
          className="px-3 py-1.5 text-base font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {runMutation.isPending ? 'Running…' : 'Run Scraper'}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 text-base pt-3 border-t border-neutral-100">
        <div>
          <p className="text-xs text-neutral-400 mb-0.5">Last run</p>
          <p className="font-medium text-neutral-900">{fmt(a.scraperLastRunAt)}</p>
        </div>
        {sd.dataSource && (
          <>
            <div>
              <p className="text-xs text-neutral-400 mb-0.5">Last status</p>
              <p
                className={`text-base font-medium ${SYNC_STATUS_STYLES[sd.dataSource.lastRunStatus] ?? 'text-neutral-500'}`}
              >
                {sd.dataSource.lastRunStatus ?? '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-400 mb-0.5">Last run at</p>
              <p className="text-base text-neutral-700">{fmt(sd.dataSource.lastRunAt)}</p>
            </div>
            {sd.dataSource.lastRunStats && (
              <div className="col-span-2">
                <p className="text-xs text-neutral-400 mb-1">Last run stats</p>
                <div className="flex gap-4 text-base">
                  <span>
                    <span className="font-medium text-emerald-600">
                      {sd.dataSource.lastRunStats.created ?? 0}
                    </span>{' '}
                    created
                  </span>
                  <span>
                    <span className="font-medium text-blue-600">
                      {sd.dataSource.lastRunStats.updated ?? 0}
                    </span>{' '}
                    updated
                  </span>
                  <span>
                    <span className="font-medium text-amber-600">
                      {sd.dataSource.lastRunStats.total ?? 0}
                    </span>{' '}
                    total
                  </span>
                  <span>
                    <span className="font-medium text-red-600">
                      {sd.dataSource.lastRunStats.errors ?? 0}
                    </span>{' '}
                    errors
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {runError && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-base text-red-700">
          {runError}
        </div>
      )}
      {runResult && (
        <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-base text-emerald-700">
          Completed — {runResult.total} elections parsed, {runResult.created} created,{' '}
          {runResult.updated} updated, {runResult.errors} errors
          {runResult.pages?.length > 0 && (
            <span className="ml-2 text-emerald-600">
              · {runResult.pages.length} calendar page
              {runResult.pages.length !== 1 ? 's' : ''} discovered
            </span>
          )}
        </div>
      )}

      {(() => {
        const pages = a?.scraperConfig?.pages;
        if (!Array.isArray(pages) || pages.length === 0) return null;
        return (
          <div className="mt-4 pt-4 border-t border-neutral-100">
            <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">
              Discovered pages ({pages.length})
            </h4>
            <div className="border border-neutral-200 rounded-lg overflow-hidden divide-y divide-neutral-100">
              {pages.map((p, i) => (
                <div key={p.url} className="flex items-center gap-3 px-3 py-2 text-xs">
                  {i === 0 && (
                    <span className="shrink-0 px-1.5 py-0.5 bg-sky-100 text-sky-700 rounded font-medium">
                      primary
                    </span>
                  )}
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 truncate flex-1"
                    title={p.url}
                  >
                    {p.label || p.url}
                  </a>
                  <span className="shrink-0 text-neutral-400">
                    {p.electionCount} election{p.electionCount !== 1 ? 's' : ''}
                  </span>
                  <span className="shrink-0 text-neutral-300">depth {p.depth}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {syncLogs.length > 0 && (
        <div className="mt-4 pt-4 border-t border-neutral-100">
          <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">
            Run history
          </h4>
          <div className="border border-neutral-200 rounded-lg overflow-hidden divide-y divide-neutral-100">
            {syncLogs.map((log) => {
              const logEnd = log.finishedAt ? new Date(log.finishedAt) : new Date();
              const logStart = new Date(log.startedAt);
              const logElections = (sd.elections ?? []).filter((e) => {
                const t = new Date(e.lastSyncedAt);
                return t >= logStart && t <= logEnd;
              });
              return <RunLogRow key={log.id} log={log} elections={logElections} />;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SiteChecksSection({ checks }) {
  return checks.length === 0 ? (
    <p className="text-base text-neutral-400 italic text-center py-8">No checks recorded yet.</p>
  ) : (
    <div className="overflow-x-auto">
      <table className="w-full text-base">
        <thead>
          <tr className="border-b border-neutral-200">
            <th className="text-left px-3 py-2 text-xs font-semibold text-neutral-500">Time</th>
            <th className="text-left px-3 py-2 text-xs font-semibold text-neutral-500">HTTP</th>
            <th className="text-left px-3 py-2 text-xs font-semibold text-neutral-500">Response</th>
            <th className="text-left px-3 py-2 text-xs font-semibold text-neutral-500">Change?</th>
            <th className="text-left px-3 py-2 text-xs font-semibold text-neutral-500">Score</th>
            <th className="text-left px-3 py-2 text-xs font-semibold text-neutral-500">Error</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {checks.map((c) => (
            <tr key={c.id} className={c.changeDetected ? 'bg-amber-50' : ''}>
              <td className="px-3 py-2 text-xs text-neutral-600 whitespace-nowrap">
                {fmt(c.checkedAt)}
              </td>
              <td className="px-3 py-2">
                {c.httpStatus ? (
                  <span
                    className={`text-xs font-medium ${c.httpStatus < 400 ? 'text-emerald-700' : 'text-red-600'}`}
                  >
                    {c.httpStatus}
                  </span>
                ) : (
                  '—'
                )}
              </td>
              <td className="px-3 py-2 text-xs text-neutral-500">{ms(c.responseTimeMs)}</td>
              <td className="px-3 py-2">
                {c.changeDetected ? (
                  <span className="text-xs font-medium text-amber-600">Yes</span>
                ) : (
                  <span className="text-xs text-neutral-400">No</span>
                )}
              </td>
              <td className="px-3 py-2 text-xs text-neutral-500">
                {c.changeSignificanceScore != null ? c.changeSignificanceScore : '—'}
              </td>
              <td className="px-3 py-2 text-xs text-red-500 max-w-xs truncate">
                {c.errorMessage ?? ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminAuthorityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [runError, setRunError] = useState(null);
  const [runResult, setRunResult] = useState(null);

  usePageTitle('Jurisdiction Workspace');

  if (user && user.role !== 'admin') {
    navigate('/');
    return null;
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: ['authority-detail', id],
    queryFn: () => authoritiesAPI.get(id),
  });

  const runMutation = useMutation({
    mutationFn: () => authoritiesAPI.runScraper(id),
    onSuccess: (res) => {
      setRunError(null);
      setRunResult({ ...res.result, pages: res.pages ?? [] });
      qc.invalidateQueries({ queryKey: ['authority-detail', id] });
    },
    onError: (err) => {
      setRunResult(null);
      setRunError(err.response?.data?.message ?? 'Scraper failed');
    },
  });

  const a = data?.authority;
  const sd = data?.scraperData;
  const checks = a?.websiteChecks ?? [];

  return (
    <DetailPageLayout
      title={a?.name}
      breadcrumbs={[
        { label: 'Election Authorities', url: '/admin/authorities' },
        { label: a?.name },
      ]}
      isLoading={isLoading}
      error={isError}
      hero={
        a && (
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-base text-neutral-500 mb-2">
                  {a.state} · {a.jurisdictionType}
                </p>
              </div>
              <span
                className={`px-3 py-1 text-base font-medium rounded-full whitespace-nowrap ${SCRAPER_STYLES[a.scraperStatus] ?? 'bg-neutral-100 text-neutral-400'}`}
              >
                {a.scraperStatus}
              </span>
            </div>
          </div>
        )
      }
      sections={[
        {
          title: 'Official Sources',
          content: a && <AdminOfficalSources authority={a} authorityId={id} qc={qc} />,
        },
        {
          title: 'Voter Preparedness',
          content: a && <AdminVoterPreparedness authority={a} authorityId={id} qc={qc} />,
        },
        {
          title: 'Elections',
          content: sd && <AdminElectionEditor scraperData={sd} authorityId={id} />,
        },
        {
          title: 'Scraper',
          content: (
            <ScraperSection
              authority={a}
              sd={sd}
              runMutation={runMutation}
              runError={runError}
              runResult={runResult}
              setRunError={setRunError}
              setRunResult={setRunResult}
            />
          ),
        },
        {
          title: `Recent site checks (${checks.length})`,
          content: <SiteChecksSection checks={checks} />,
        },
      ]}
    />
  );
}
